import { FC } from "react";
import type { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { newProductTransformer, productDetailsTransFormer } from "../../utils";

import {
  ProductEntity,
  ProductResponse,
} from "../../types/product_entity_types";
import { ProductDetails } from "../../src/templates";
import { MoreLikeThis } from "../../src/templates/MoreLikeThis";
import { captureException } from "@sentry/nextjs";
import Error from "next/error";
import { NoItemsFound } from "../../src/atoms";
import { SlClose } from "react-icons/sl";
import { Container, Stack } from "@mui/material";
import { groq } from "next-sanity";
import { client } from "../../sanity/lib/client";

const getTerracottaPolicy = () => {
  return groq`*[_type == "policy" && jsonName == "faq-terracotta"][0]`;
};

const getPDPPageStaticContent = () => {
  return groq`*[_type == "pdp"][0]`;
};

export const getStaticProps: GetStaticProps = async context => {
  const { params } = context;
  const response = await fetch(
    `${process.env.BASE_URL}/listings/items/search/details?query=${params!.id}`
  );
  const data = (await response.json()) as ProductResponse;
  if (!response.ok) {
    captureException(data);
    throw new Error(
      // @ts-ignore
      `Failed to fetch posts, received status ${response.status}`
    );
  }
  const { products, moreLikeThis } = data;
  let product = null;
  if (Object.keys(products).length === 0) {
    product = null;
  } else {
    product = productDetailsTransFormer(products as ProductEntity);
  }
  const morelikeThis = moreLikeThis.map(el => newProductTransformer(el));

  let faq = null;
  if (
    Object.keys(data.products).length !== 0 &&
    // @ts-ignore
    data.products.searchKeywords.includes("Terracotta")
  ) {
    const res = await fetch(
      `https://sanity-json-file-storage.s3.ap-south-1.amazonaws.com/policy/faq-terracotta.json`
    );
    faq = await res.json();
  }

  const res = await fetch(
    `https://sanity-json-file-storage.s3.ap-south-1.amazonaws.com/pdp/pdp-page-static-content.json`
  );
  const pdpPageStaticContent = await res.json();

  let showSaleTimer = false;
  if (
    Object.keys(data.products).length !== 0 &&
    // @ts-ignore
    data.products.searchKeywords.includes("dotw")
  ) {
    showSaleTimer = true;
  }

  return {
    props: {
      eventId: data.eventId,
      product: product,
      morelikeThis,
      data,
      faq,
      showSaleTimer,
      pdpPageStaticContent,
    },
    revalidate: 300,
  };
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}

const ProductDetailsPage: FC<any> = props => {
  const {
    product,
    morelikeThis,
    eventId,
    data,
    faq,
    showSaleTimer,
    pdpPageStaticContent,
  } = props;

  return (
    <Container maxWidth="xl">
      {product ? (
        <>
          {product.seo ? (
            <NextSeo
              title={product.seo.seoTitle}
              description={product.seo.seoDescription}
              canonical={product.seo.seoCanonicalUrl}
              noindex={product.seo.noIndex}
              nofollow={product.seo.noFollow}
              facebook={{
                appId: product.seo.facebookAppId,
              }}
              twitter={{
                cardType: product.seo.twitterCardType,
                handle: product.seo.twitterHandle,
                site: product.seo.twitterSite,
              }}
              additionalMetaTags={product.seo.additionalMetaTags}
              additionalLinkTags={product.seo.additionalLinkTags}
              openGraph={{
                type: product.seo.openGraphType,
                url: product.seo.openGraphUrl,
                title: product.seo.openGraphTitle,
                description: product.seo.openGraphDescription,
                images: [
                  {
                    url: product.seo.openGraphImages,
                    width: 200,
                    height: 200,
                    alt: "Og Image Alt",
                  },
                ],
                locale: product.seo.openGraphLocale,
              }}
              robotsProps={product.seo.robotsProps}
            />
          ) : null}
          <Stack gap={6}>
            <ProductDetails
              product={product}
              eventId={eventId}
              boughtTogetherProducts={morelikeThis.slice(0, 2)}
              faq={faq}
              showSaleTimer={showSaleTimer}
              pdpPageStaticContent={pdpPageStaticContent}
            />
            <MoreLikeThis products={morelikeThis} />
          </Stack>
        </>
      ) : (
        <NoItemsFound
          title="Product Not Found"
          message="This may be an old or invalid product."
          icon={<SlClose />}
        />
      )}
    </Container>
  );
};

export default ProductDetailsPage;
